// src/components/annotations/AnnotationGeometryEditor.jsx
//
// Drives the shared MlDraw instance for vertex-editing ONE annotation
// polygon. Renders nothing; it is a coordination effect with a component's
// lifecycle, exactly like PadRoadDrawer.
//
// ── WHAT MlDraw CAN VERTEX-EDIT ──────────────────────────────────────
// Polygons and LineStrings, via _editCoords in ml-draw.js. Points are not
// in that set and never will be — a point has one coordinate, so it is
// dragged directly by useAnnotationLayer instead.
//
// The gate below matters because failure here is silent rather than loud:
// handing MlDraw a geometry it cannot edit is not an error, it just draws
// no handles, and the user is left clicking at nothing.
//
// ── SHARING ONE MlDraw INSTANCE ──────────────────────────────────────
// MapView owns a single instance (MapView.jsx:598) shared by AOI editing,
// PadRekko road drawing and now this. There is no mutex; consumers
// cooperate by mode-gating and by restoring `onUpdate` on cleanup. Two
// rules follow, both load-bearing:
//
//   1. Yield to AOI mode. The AOI effects (MapView.jsx:1394-1417) load the
//      active AOI's geometry into the same single-feature slot whenever
//      aoiModeActive. If we engaged at the same time, whichever ran last
//      would own the slot and the other's handles would act on the wrong
//      geometry. AOI mode wins because it is the older, broader context.
//
//   2. Chain and restore onUpdate. MapPage uses it to bump drawVersion, so
//      dropping it stops unrelated redraws. Snapshot on entry, call
//      through, restore on exit — the PadRoadDrawer contract.

import { useEffect } from "react";
import { useAnnotationStore } from "../../stores/useAnnotationStore";

// Geometry types MlDraw's vertex machinery understands. Kept in one place
// because three things have to agree: this gate, the write-back guard
// below, and useAnnotationLayer's decision to withhold the feature it is
// editing. If they drift, the symptom is a feature that vanishes from the
// map with nothing drawing it in MlDraw's place.
const EDITABLE_GEOMETRY = new Set(["Polygon", "LineString"]);

export default function AnnotationGeometryEditor({ drawInstance, aoiModeActive }) {
  const open = useAnnotationStore(s => s.open);
  const editingId = useAnnotationStore(s => s.editingId);

  useEffect(() => {
    if (!drawInstance) return;
    if (!open || !editingId) return;
    if (aoiModeActive) return;                       // rule 1

    // Read the annotation once, imperatively. Subscribing to `annotations`
    // would re-run this effect on every geometry write-back below and
    // restart the edit session mid-drag.
    const ann = useAnnotationStore.getState().annotations.find(a => a.id === editingId);
    if (!ann || !EDITABLE_GEOMETRY.has(ann.geojson?.geometry?.type)) return;

    const { updateAnnotationGeometry } = useAnnotationStore.getState();

    // Aliased to a local before mutating. MlDraw is an external imperative
    // object rather than React state, so swapping its callback is the
    // intended way to drive it — but react-hooks/immutability sees a prop
    // being assigned to and rejects it (PadRoadDrawer.jsx:56 carries that
    // error today). The alias says the mutation is of the draw instance,
    // not of this component's props.
    const draw = drawInstance;
    const prevOnUpdate = draw.onUpdate;              // rule 2

    const handleUpdate = () => {
      prevOnUpdate?.();
      const f = draw.lastFeature;
      if (!f || !EDITABLE_GEOMETRY.has(f.geometry?.type)) return;
      updateAnnotationGeometry(editingId, f.geometry);
    };

    draw.onUpdate = handleUpdate;

    // Clear any carry-over before claiming the slot — same order
    // PadRoadDrawer uses, and the same reason: whatever the previous
    // consumer left behind is not ours to edit.
    draw.stopDrawing?.();
    draw.delete?.();
    draw.add({
      type: "Feature",
      geometry: ann.geojson.geometry,
      properties: {},
    });
    draw.enterVertexEdit?.();

    return () => {
      draw.onUpdate = prevOnUpdate ?? null;
      draw.exitVertexEdit?.();
      draw.delete?.();
    };
    // `annotations` is deliberately not a dependency — see the note above.
  }, [drawInstance, open, editingId, aoiModeActive]);

  // If AOI mode takes over while we are editing, drop the claim so the
  // panel button reverts and useAnnotationLayer resumes drawing the
  // feature. Without this the annotation stays hidden with nothing
  // editing it — invisible until the user deselects.
  useEffect(() => {
    if (aoiModeActive && editingId) {
      useAnnotationStore.getState().setEditingId(null);
    }
  }, [aoiModeActive, editingId]);

  return null;
}
